import { useState, useEffect, useCallback, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { Category, PlanEvent, RecurrenceRule } from "../../../shared/types";
import { useCategories } from "../../../shared/stores/category-store";
import { DAY_LABELS, WEEK_ORDER } from "../utils/derive-template";
import { toDateString } from "../../../shared/types/date-helpers";
import { CATEGORY_DOT_COLORS } from "../../../shared/types";
import { CustomSelect } from "../../../ui/atoms/CustomSelect";
import { TimePicker } from "../../../ui/atoms/TimePicker";

const DAY_SHORT_LABELS = ["D", "L", "M", "M", "J", "V", "S"] as const;

type RecurrenceType = RecurrenceRule["type"];

interface ScheduleAddSlotFormProps {
  readonly defaultDay: number;
  readonly defaultRecurrenceType?: RecurrenceType;
  readonly defaultCategory: Category;
  readonly onAdd: (data: Omit<PlanEvent, "id" | "createdAt">) => void;
  readonly onClose: () => void;
}

function getNextDateForDay(dayOfWeek: number): string {
  const today = new Date();
  const currentDay = today.getDay();
  let diff = dayOfWeek - currentDay;
  if (diff <= 0) diff += 7;
  const target = new Date(today);
  target.setDate(today.getDate() + diff);
  return toDateString(target);
}

const PRIMARY_OPTIONS: readonly { value: RecurrenceType; label: string }[] = [
  { value: "daily", label: "Quotidien" },
  { value: "weekly", label: "Hebdo" },
  { value: "monthly", label: "Mensuel" },
];

const EXTRA_OPTIONS: readonly { value: RecurrenceType; label: string }[] = [
  { value: "quarterly", label: "Trimestriel" },
  { value: "semiannual", label: "Semestriel" },
  { value: "yearly", label: "Annuel" },
];

function DayPicker({
  value,
  onChange,
  label = "Jours",
}: {
  readonly value: readonly number[];
  readonly onChange: (days: readonly number[]) => void;
  readonly label?: string;
}): ReactNode {
  const handleDayToggle = (day: number) => {
    const isSelected = value.includes(day);
    let newDays: readonly number[];
    if (isSelected && value.length > 1) {
      newDays = value.filter((d) => d !== day);
    } else if (!isSelected) {
      newDays = [...value, day].sort((a, b) => a - b);
    } else {
      return;
    }
    onChange(newDays);
  };

  return (
    <div>
      <label className="text-[10px] text-ink-faint uppercase tracking-wide mb-1.5 block">
        {label}
      </label>
      <div className="flex gap-1.5">
        {DAY_SHORT_LABELS.map((label, index) => {
          const isActive = value.includes(index);
          return (
            <button
              key={index}
              type="button"
              onClick={() => handleDayToggle(index)}
              className={`w-8 h-8 rounded-lg text-[11px] font-medium transition-colors
                ${isActive
                  ? "bg-accent text-white"
                  : "bg-surface-alt text-ink-muted border border-border/60 hover:border-accent/50"
                }`}
              >
                {label}
              </button>
          );
        })}
      </div>
    </div>
  );
}

function RecurrenceSelector({
  value,
  onChange,
  showMore,
  onToggleMore,
}: {
  readonly value: RecurrenceType;
  readonly onChange: (v: RecurrenceType) => void;
  readonly showMore: boolean;
  readonly onToggleMore: () => void;
}): ReactNode {
  const btnCls = (v: RecurrenceType) =>
    `flex-1 px-2 py-1.5 text-xs rounded-md transition-colors ${
      value === v ? "bg-accent text-white" : "bg-surface-alt text-ink-muted hover:text-ink"
    }`;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-1">
        {PRIMARY_OPTIONS.map((opt) => (
          <button key={opt.value} type="button" onClick={() => onChange(opt.value)} className={btnCls(opt.value)}>
            {opt.label}
          </button>
        ))}
        <button
          type="button"
          onClick={onToggleMore}
          className="px-2 py-1.5 text-xs rounded-md bg-surface-alt text-ink-muted hover:text-ink transition-colors"
          title="Plus de récurrences"
        >
          {showMore ? "−" : "+"}
        </button>
      </div>
      {showMore && (
        <div className="flex gap-1">
          {EXTRA_OPTIONS.map((opt) => (
            <button key={opt.value} type="button" onClick={() => onChange(opt.value)} className={btnCls(opt.value)}>
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ScheduleAddSlotForm({
  defaultDay,
  defaultRecurrenceType = "weekly",
  defaultCategory,
  onAdd,
  onClose,
}: ScheduleAddSlotFormProps) {
  const { categories, labels } = useCategories();
  const [title, setTitle] = useState("");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [category, setCategory] = useState<Category>(defaultCategory);
  const [selectedDays, setSelectedDays] = useState<readonly number[]>([defaultDay]);
  const [recurrenceType, setRecurrenceType] = useState<"daily" | "weekly" | "monthly" | "quarterly" | "semiannual" | "yearly">(defaultRecurrenceType);
  const [showMoreRecurrence, setShowMoreRecurrence] = useState(
    ["quarterly", "semiannual", "yearly"].includes(defaultRecurrenceType),
  );

  // Reset selectedDays to single day when switching from weekly
  const handleRecurrenceTypeChange = (newType: RecurrenceType) => {
    setRecurrenceType(newType);
    if (newType !== "weekly" && selectedDays.length > 1) {
      // Keep only the first selected day when switching away from weekly
      setSelectedDays([selectedDays[0] ?? 1]);
    }
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const handleSubmit = () => {
    if (!title.trim()) return;

    // Get the first (or next) occurrence date
    const firstSelectedDay = selectedDays[0] ?? 1;
    const date = recurrenceType === "daily"
      ? toDateString(new Date())
      : getNextDateForDay(firstSelectedDay);

    // Build recurrence rule with daysOfWeek for weekly
    const recurrence: RecurrenceRule =
      recurrenceType === "weekly" && selectedDays.length > 0
        ? { type: recurrenceType, interval: 1, endDate: null, daysOfWeek: selectedDays }
        : { type: recurrenceType, interval: 1, endDate: null };

    onAdd({
      title: title.trim(),
      category,
      date,
      startTime,
      endTime,
      description: "",
      notes: "",
      tags: [],
      recurrence,
      reminderMinutes: null,
      reminderTime: null,
      reminderType: null,
    });
    onClose();
  };

  const inputCls =
    "w-full px-3 py-2 text-xs bg-surface border border-border/60 rounded-md text-ink focus:outline-none focus:border-accent/60";

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-surface border border-border/60 rounded-2xl shadow-xl w-[340px] max-w-[92vw] p-4 space-y-3 my-auto"
        onClick={(e) => e.stopPropagation()}
        data-ph-no-capture
      >
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-ink">
            Nouveau creneau
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full text-ink-muted hover:text-ink hover:bg-surface-alt text-sm"
          >
            ×
          </button>
        </div>

        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Titre du creneau"
          autoFocus
          className={inputCls}
        />

        <div className="flex gap-2">
          <div className="flex-1">
            <label className="text-[10px] text-ink-faint uppercase tracking-wide mb-1.5 block">
              Début
            </label>
            <TimePicker value={startTime} onChange={setStartTime} />
          </div>
          <div className="flex-1">
            <label className="text-[10px] text-ink-faint uppercase tracking-wide mb-1.5 block">
              Fin
            </label>
            <TimePicker value={endTime} onChange={setEndTime} />
          </div>
        </div>

        <div className="flex gap-2">
          <div className="flex-1">
            <label className="text-[10px] text-ink-faint uppercase tracking-wide mb-1.5 block">
              Catégorie
            </label>
            <CustomSelect
              value={category}
              onChange={(val) => setCategory(val as Category)}
              options={categories.map((cat) => ({
                value: cat,
                label: labels[cat],
                dot: CATEGORY_DOT_COLORS[cat],
              }))}
              className="w-full"
            />
          </div>
          {recurrenceType !== "daily" && recurrenceType !== "weekly" && (
            <div className="flex-1">
              <label className="text-[10px] text-ink-faint uppercase tracking-wide mb-1.5 block">
                Jour
              </label>
              <CustomSelect
                value={(selectedDays[0] ?? 1).toString()}
                onChange={(val) => setSelectedDays([Number(val)])}
                options={WEEK_ORDER.map((d) => ({
                  value: d.toString(),
                  label: DAY_LABELS[d],
                }))}
                className="w-full"
              />
            </div>
          )}
        </div>

        {recurrenceType === "weekly" && (
          <DayPicker
            value={selectedDays}
            onChange={setSelectedDays}
            label="Jours de la semaine"
          />
        )}

        <div>
          <label className="text-[10px] text-ink-faint uppercase tracking-wide mb-1.5 block">
            Récurrence
          </label>
          <RecurrenceSelector
            value={recurrenceType}
            onChange={handleRecurrenceTypeChange}
            showMore={showMoreRecurrence}
            onToggleMore={() => setShowMoreRecurrence((v) => !v)}
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-xs font-medium text-ink-muted border border-border/60 rounded-md hover:bg-surface-alt transition-colors"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!title.trim()}
            className="px-3 py-2 text-xs font-medium bg-accent text-white rounded-md hover:opacity-80 transition-opacity disabled:opacity-40"
          >
            Ajouter
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
